import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Backdrop,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EnhancedTable from "../../Components/EventsComponents/EventsTableComponent";
import EventsCalendarComponent from "../../Components/EventsComponents/EventsCalendarComponent";
import { useNavigate } from "react-router-dom";
import events_service from "../../services/events_service";
import { useDispatch, useSelector } from "react-redux";
import { setEventsList, setSelectedDay, setSelectedMonth } from "../../reducers/event_reducer";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store/store";
import { format } from "date-fns";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const StyledRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  paddingBottom: theme.spacing(2),
}));

const StyledContainer = styled(Box)({
  width: "98%",
  backgroundColor: "#fff",
  padding: "20px",
});

const TitlesContainer = styled(Box)(({ theme }) => ({
  width: "98%",
  marginBottom: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#4D67EB",
  fontSize: "12px",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#3755a3",
  },
  "& .MuiSvgIcon-root": {
    marginRight: theme.spacing(1),
  },
}));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: "#fff",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
}));

const StyledChip = styled(Chip)({
  backgroundColor: "#4D67EB",
  color: "white",
  "&:hover": {
    backgroundColor: "#3755a3",
  },
});

const TabsContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderBottom: "1px solid #e0e0e0",
}));

const StyledTabs = styled(Tabs)({
  "& .MuiTabs-indicator": {
    backgroundColor: "#4D67EB",
  },
});

const StyledTab = styled(Tab)(({ selected }: { selected?: boolean }) => ({
  textTransform: "none",
  fontSize: "16px",
  minWidth: "120px",
  ...(selected && {
    color: "#4D67EB !important",
    fontWeight: "bold",
  }),
}));

const EventsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true); // Loader state
  const { t } = useTranslation();
  const { selectedDay, selectedMonth } = useSelector((state: RootState) => state.eventsReducer);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  
  const getCurrentMonthLabel = (monthStr?: string | null) => {
    if (monthStr) {
      const [month, year] = monthStr.split('/');
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return format(date, "MMMM yyyy");
    }
    return format(new Date(), "MMMM yyyy");
  };

  const handleShowAllEvents = () => {
    setShowAllEvents(true);
    dispatch(setSelectedDay(null));
    dispatch(setSelectedMonth(null));
  };

  const handleShowCurrentMonth = () => {
    setShowAllEvents(false);
    dispatch(setSelectedDay(null));
    const currentMonth = format(new Date(), "MM/yyyy");
    dispatch(setSelectedMonth(currentMonth));
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: "upcoming" | "past") => {
    setActiveTab(newValue);
    // Clear day selection when switching tabs
    dispatch(setSelectedDay(null));
    // Keep the current month filter for both tabs
    // Month filter is managed by the calendar component
  };

  useEffect(() => {
    events_service
      .getEventsList()
      .then((res) => {
        dispatch(setEventsList(res.data.status.events));
        setLoading(false); // Deactivate loader
      })
      .catch(() => {
        setLoading(false); // Deactivate loader in case of error
        MySwal.fire({
          title: "Error",
          text: "Failed to fetch events.",
          icon: "error",
          confirmButtonText: "OK",
          backdrop: `rgba(0,0,0,0.5)`
        });
      });
  }, [dispatch]);

  // Set default month filter when component mounts (only once — re-running this
  // on selectedMonth changes would undo "Show all events", which clears the month)
  useEffect(() => {
    // Set current month filter by default (will be overridden by calendar component)
    if (!selectedMonth) {
      dispatch(setSelectedMonth(format(new Date(), "MM/yyyy")));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <StyledRoot>
      <StyledBackdrop open={loading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
      <TitlesContainer>
        <Typography variant="h6">{t("Events & Schedules")}</Typography>
      </TitlesContainer>
      <StyledContainer>
        <ButtonContainer>
          <AddEventButton />
        </ButtonContainer>
        {/* Event Tabs */}
        <TabsContainer>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
          >
            <StyledTab
              value="upcoming"
              label={t("Upcoming")}
              selected={activeTab === "upcoming"}
            />
            <StyledTab
              value="past"
              label={t("Past")}
              selected={activeTab === "past"}
            />
          </StyledTabs>
        </TabsContainer>

        <Box>
          <EventsCalendarComponent />
        </Box>
        <FilterContainer>
          {selectedDay && (
            <StyledChip
              label={`Filtered by day: ${format(new Date(selectedDay), "dd/MM/yyyy")}`}
              onDelete={handleShowCurrentMonth}
              deleteIcon={<span style={{ color: "white" }}>×</span>}
            />
          )}
          {!selectedDay && !showAllEvents && selectedMonth && (
            <StyledChip
              label={`Filtered by: ${getCurrentMonthLabel(selectedMonth)}`}
              onDelete={handleShowAllEvents}
              deleteIcon={<span style={{ color: "white" }}>×</span>}
            />
          )}
          {!selectedDay && showAllEvents && (
            <StyledChip
              label="Showing all events"
              onDelete={handleShowCurrentMonth}
              deleteIcon={<span style={{ color: "white" }}>×</span>}
            />
          )}
        </FilterContainer>
        <Box>
          <EnhancedTable showAllEvents={showAllEvents} eventTimeFilter={activeTab} />
        </Box>
      </StyledContainer>
    </StyledRoot>
  );
};

const AddEventButton: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <StyledButton
      variant="contained"
      size="small"
      onClick={() => {
        navigate("/events/create");
      }}
    >
      <AddCircleOutlineIcon />
      {t("Add New Event")}
    </StyledButton>
  );
};

export default EventsPage;